import {
	useState,
	type ComponentProps,
	forwardRef,
	useRef,
	useImperativeHandle,
	MouseEvent,
	KeyboardEvent
} from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from '@/lib/classNames';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import {
	CheckboxChecked,
	CheckboxIndeterminate,
	CheckboxUnchecked,
	MagnifyingGlassGold
} from '@/resources/assets/images';

const inputVariants = tv({
	base: cn(
		'relative flex items-center justify-center px-2 transition-colors py-1 gap-2 border border-dm-black3.5',
		'data-[type="checkbox"]:border-none data-[type="checkbox"]:p-0 data-[type="checkbox"]:aspect-square data-[type="checkbox"]:w-auto data-[type="checkbox"]:cursor-pointer',
		'data-[type="radio"]:border-none data-[type="radio"]:p-0 data-[type="radio"]:aspect-square data-[type="radio"]:w-auto data-[type="radio"]:cursor-pointer'
	),
	variants: {
		hasError: {
			true: 'border-red-600 bg-red-100'
		},
		disabled: {
			true: 'cursor-not-allowed bg-warm-gray-200 opacity-50'
		},
		size: {
			sm: 'h-8 text-xs [&>input]:text-xs [&>input]:placeholder:text-xs data-[type="radio"]:h-4 data-[type="radio"]:w-4 data-[type="checkbox"]:h-4 data-[type="checkbox"]:w-4',
			md: 'h-10 text-sm [&>input]:text-sm [&>input]:placeholder:text-sm  data-[type="radio"]:h-5 data-[type="radio"]:w-5 data-[type="checkbox"]:h-5 data-[type="checkbox"]:w-5',
			lg: 'h-12 text-base [&>input]:text-base [&>input]:placeholder:text-base'
		},
		fullWidth: {
			true: 'w-full whitespace-nowrap',
			false: 'w-auto'
		},
		rounded: {
			sm: 'rounded-sm',
			md: 'rounded-md',
			lg: 'rounded-lg',
			full: 'rounded-full'
		},
		color: {
			contrast: 'bg-dm-white text-dm-black',
			default:
				'bg-dm-black3 text-dm-white data-[type="radio"]:bg-transparent data-[type="checkbox"]:bg-transparent'
		},
		padding: {
			sm: 'px-1',
			md: 'px-2',
			lg: 'px-3'
		}
	},
	defaultVariants: {
		size: 'md',
		design: 'square',
		fullWidth: false,
		rounded: 'sm',
		padding: 'md',
		color: 'default'
	}
});

type InputCustomProps = {
	isLoading?: boolean;
	hasError?: boolean;
	indeterminate?: boolean;
};

export type InputProps = Omit<ComponentProps<'input'>, 'size'> &
	VariantProps<typeof inputVariants> &
	InputCustomProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			size,
			rounded,
			padding,
			fullWidth,
			isLoading,
			hasError,
			type,
			disabled,
			color,
			indeterminate = false,
			className,
			...props
		},
		ref
	) => {
		const { checked = false } = props;
		const [inputType, setInputType] = useState(type);

		const inputRef = useRef<HTMLInputElement>(null);

		const toggleInputType = () => {
			setInputType(prevType =>
				prevType === 'password' ? 'text' : 'password'
			);
		};

		useImperativeHandle(ref, () => inputRef.current!, []);

		if (isLoading) {
			return (
				<div className="border-warm-gray-300 bg-warm-gray-200 flex h-12 w-full animate-pulse cursor-not-allowed border" />
			);
		}

		const handleInputFocus = (
			e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>
		) => {
			if (type === 'checkbox' || type === 'radio') {
				e.preventDefault();
				e.stopPropagation();

				inputRef.current?.click();
			}

			inputRef.current?.focus();
		};

		return (
			<div
				role="button"
				tabIndex={0}
				onClick={handleInputFocus}
				onKeyDown={handleInputFocus}
				data-type={type}
				className={inputVariants({
					size,
					padding,
					fullWidth,
					rounded,
					className,
					disabled,
					hasError,
					color
				})}
			>
				<input
					{...props}
					type={inputType}
					ref={inputRef}
					disabled={disabled}
					data-is-contrast-color={color === 'contrast'}
					className={cn(
						'placeholder:text-dm-light-grey peer flex h-full w-full appearance-none border-0 bg-transparent p-0 shadow-none placeholder:line-clamp-1 hover:border-0 focus:border-0 focus:outline-none focus:ring-0',
						'[&[type="checkbox"]]:hidden [&[type="radio"]]:hidden',
						'data-[is-contrast-color=true]:text-dm-black data-[is-contrast-color=true]:placeholder:text-dm-strong-mid-grey'
					)}
				/>

				{type === 'checkbox' &&
					(indeterminate ? (
						<CheckboxIndeterminate />
					) : (
						<>
							<span className="hidden h-full w-full peer-checked:block">
								<CheckboxChecked className="h-full w-full" />
							</span>
							<span className="block h-full w-full peer-checked:hidden">
								<CheckboxUnchecked className="h-full w-full" />
							</span>
						</>
					))}

				{type === 'radio' && (
					<span
						data-checked={checked}
						className={cn(
							'relative h-4 w-4 rounded-full border border-dm-gray data-[checked=true]:border-dm-gold',
							"before:absolute before:inset-0 before:left-1/2 before:top-1/2 before:h-1.5 before:w-1.5 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:bg-dm-gold before:opacity-0 before:content-[''] data-[checked=true]:before:opacity-100"
						)}
					/>
				)}

				{type === 'password' && (
					<button
						type="button"
						onClick={toggleInputType}
						className="focus:ring-none flex items-center justify-center transition-transform hover:scale-110 focus:scale-110 focus:outline-none"
					>
						{inputType === 'password' ? <FaEye /> : <FaEyeSlash />}
					</button>
				)}

				{type === 'search' && (
					<MagnifyingGlassGold className="-scale-x-100" />
				)}
			</div>
		);
	}
);

Input.displayName = 'Input';
